import { lazy, Suspense, useRef } from 'react';
import { createBrowserRouter, RouterProvider as ReactRouterProvider } from 'react-router-dom';

import { RouteErrorBoundary } from '@components/route-error-boundary';
import { useGoogleAnalytics } from '@hooks/use-google-analytics';

import {
  clearOrgSelection,
  getOrgSelectionFlow,
  setNeedsOrgSelection,
  useCurrentUserQuery,
} from 'api/authentication/hooks';
import { CurrentUser } from 'api/authentication/requests';
import { usePrWaking } from 'api/request';

import { AuthenticatedNavigation } from './authenticated/navigation';

const PreboardingCompanyNavigation = lazy(() => import('./preboarding-company/navigation'));
const PreboardingUserNavigation = lazy(() => import('./preboarding-user/navigation'));
const UnauthenticatedNavigation = lazy(() => import('./unauthenticated/navigation'));
const OrganizationSelectionNavigation = lazy(() => import('./organization-selection/navigation'));
const PrWakingScreen = lazy(() =>
  import('components/pr-waking-screen').then((module) => ({ default: module.PrWakingScreen })),
);

const initOrgSelectionFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const flow = params.get('org-select');
  if (flow === 'signin' || flow === 'signup') {
    setNeedsOrgSelection(flow);
    params.delete('org-select');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }
};

const needsOrganizationSelection = (user: NonNullable<CurrentUser>): boolean => {
  const flow = getOrgSelectionFlow();
  if (!flow) return false;

  const preboardedCount = user.companies.filter((c) => c.hasFinishedPreboarding).length;

  if (flow === 'signup' && preboardedCount >= 1) return true;
  if (flow === 'signin' && preboardedCount > 1) return true;

  // Signup with a domain that matches an existing company the user isn't part of
  if (flow === 'signup' && user.hasDomainMatchingCompany) return true;

  clearOrgSelection();
  return false;
};

const Root = () => {
  const hasInitialized = useRef(false);
  if (!hasInitialized.current) {
    initOrgSelectionFromUrl();
    hasInitialized.current = true;
  }

  const currentUser = useCurrentUserQuery();
  const isWaking = usePrWaking((state) => state.isWaking);
  useGoogleAnalytics();

  if (isWaking) {
    return (
      <Suspense fallback={null}>
        <PrWakingScreen />
      </Suspense>
    );
  }

  if (currentUser.isLoading) {
    return null;
  }

  const user = currentUser.data;
  const showOrgSelector = user ? needsOrganizationSelection(user) : false;

  const Navigation = user
    ? showOrgSelector
      ? OrganizationSelectionNavigation
      : user.company.hasFinishedPreboarding
        ? user.hasFinishedPreboarding
          ? AuthenticatedNavigation
          : PreboardingUserNavigation
        : PreboardingCompanyNavigation
    : UnauthenticatedNavigation;

  return (
    <Suspense fallback={null}>
      <Navigation />
    </Suspense>
  );
};

const router = createBrowserRouter([
  {
    path: '*',
    element: <Root />,
    errorElement: <RouteErrorBoundary />,
  },
]);

export const isInTemplateRoute = () => {
  return router.state.location.pathname.startsWith('/templates/');
};

export const RouterProvider = () => <ReactRouterProvider router={router} />;
